import type { GanttData, Dependency, Task } from './../types';
import { parseDate, formatDate, diffDays, addDays } from './../dateUtils';

// ─── Topological sort helper (Kahn's algorithm) ─────────────────────────────
const topoSort = (taskIds: string[], deps: Dependency[]): string[] => {
  const inDeg = new Map<string, number>(taskIds.map(id => [id, 0]));
  const adj   = new Map<string, string[]>(taskIds.map(id => [id, []]));

  deps.forEach(d => {
    if (!inDeg.has(d.fromTaskId) || !inDeg.has(d.toTaskId)) return;
    adj.get(d.fromTaskId)!.push(d.toTaskId);
    inDeg.set(d.toTaskId, (inDeg.get(d.toTaskId) ?? 0) + 1);
  });

  const queue = taskIds.filter(id => inDeg.get(id) === 0);
  const result: string[] = [];

  while (queue.length > 0) {
    const id = queue.shift()!;
    result.push(id);
    adj.get(id)!.forEach(next => {
      const deg = (inDeg.get(next) ?? 1) - 1;
      inDeg.set(next, deg);
      if (deg === 0) queue.push(next);
    });
  }

  // Append any tasks caught in cyclical dependencies so they aren't lost
  const inResult = new Set(result);
  taskIds.forEach(id => { if (!inResult.has(id)) result.push(id); });

  return result;
};

// ─── Main Function ─────────────────────────────────────────────────────────

export const shiftFromUnresolvedDependencies = (state: GanttData): GanttData => {
  // Create a mutable map of tasks to track their updated dates as we ripple forward
  const taskMap = new Map<string, Task>(
    state.tasks.map(t => [t.id, { ...t }])
  );

  // Map incoming dependencies for quick lookup
  const incomingDeps = new Map<string, Dependency[]>(
    state.tasks.map(t => [t.id, []])
  );
  state.dependencies.forEach(d => {
    incomingDeps.get(d.toTaskId)?.push(d);
  });

  // Sort tasks topologically so we resolve earlier tasks before later ones
  const sortedIds = topoSort(state.tasks.map(t => t.id), state.dependencies);

  sortedIds.forEach(taskId => {
    const task = taskMap.get(taskId);
    if (!task) return;

    const deps = incomingDeps.get(taskId) ?? [];
    if (deps.length === 0) return;

    // Track the current start timestamp of the task
    const currentStartMs = parseDate(task.startDate).getTime();
    let maxConstraintMs = currentStartMs; // Start by assuming we don't need to move

    deps.forEach(dep => {
      const fromTask = taskMap.get(dep.fromTaskId);
      if (!fromTask) return;

      // ⚠️ ADAPT THIS to match your exact "unresolved" logic
      const isUnresolved = !fromTask.isResolved;
      
      // We only care about pushing tasks forward if the incoming task is UNRESOLVED
      if (isUnresolved) {
        // Assume default Finish-to-Start (FS) dependency: 
        // toTask.start >= fromTask.end
        const fromEndMs = parseDate(fromTask.endDate).getTime();
        
        if (fromEndMs > maxConstraintMs) {
          maxConstraintMs = fromEndMs;
        }
      }
    });

    // If a constraint forces the task to start later than it currently does, shift it
    if (maxConstraintMs > currentStartMs) {
      const durationDays = Math.max(1, diffDays(task.startDate, task.endDate));
      
      const newStartIso = formatDate(new Date(maxConstraintMs));
      const newEndIso = addDays(newStartIso, durationDays);

      task.startDate = newStartIso;
      task.endDate = newEndIso;
    }
  });

  // Return updated state, converting the map back to an array
  return {
    ...state,
    tasks: state.tasks.map(t => taskMap.get(t.id)!)
  };
};
